import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';
import { httpOptions } from '../constants/http-options.constant';
import { ApiMyProfile, MyProfile, adaptMyProfile, ChangeEmailRequest, ChangeEmailResult } from '../models';

@Injectable({
  providedIn: 'root'
})
export class AuthnProfileService {
  private _http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private baseUrl = requireHostUrl(this.config, 'authn');

  // Stato condiviso del profilo: popolato da loadProfile() (chiamato dal parent
  // AccountSettingsComponent). Consumato dai child (ProfileView, ChangePassword
  // gating) e da AccountSettingsComponent stesso per il loading skeleton.
  private readonly _profile = signal<MyProfile | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal(false);
  readonly profile = this._profile.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  /**
   * Idempotent: scatena UN solo fetch del profilo. Successive chiamate (da
   * parent o altri consumer) sono no-op finché c'è già profilo o un fetch
   * in corso. Aggiorna i signal `loading`, `error`, `profile` di conseguenza.
   */
  loadProfile(): void {
    if (this._loading() || this._profile()) {
      return;
    }
    this._loading.set(true);
    this._error.set(false);
    this.getMyProfile().subscribe({
      next: () => this._loading.set(false),
      error: () => {
        this._loading.set(false);
        this._error.set(true);
      }
    });
  }

  getMyProfile(): Observable<MyProfile> {
    const url = `${this.baseUrl}/v1/me/profile`;

    return this._http.get<ApiMyProfile>(url, httpOptions).pipe(
      map(adaptMyProfile),
      tap(profile => this._profile.set(profile)),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const url = `${this.baseUrl}/v1/me/change-password`;
    const body = { currentPassword, newPassword };

    return this._http.post<void>(url, body, httpOptions).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  changeEmail(newEmail: string): Observable<ChangeEmailResult> {
    const url = `${this.baseUrl}/v1/me/change-email`;
    const body: ChangeEmailRequest = { newEmail };

    return this._http.post<ChangeEmailResult>(url, body, httpOptions).pipe(
      tap(() => {
        // Invalida cache profilo: prossimo loadProfile rifetcha dal BE.
        this._profile.set(null);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }
}
